import {useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import classNames from "classnames";
import * as Api from "utils/api/api.js";
import * as reduxSelectors from "components/store/application.reducers";
import {DETAILS_VIEW_TYPE} from "utils/constants";
import {Loader, LoaderSize} from "components/common/Loader.component";
import {Notification, NotificationType} from "components/common/Notification.component";
import {
    BNXT_LOG_PREFIX,
    BnxtEditorTarget,
    BnxtEditRequest,
    buildOpenTaskMessage,
    isEditorReadyMessage,
    OPEN_TASK_SUCCESS,
    parseEditorUrl,
    readOpenTaskResult
} from "./bnxtOpenTask";

interface BnxtDocumentEditorProps {
    taskId: string;
    id: number | string | null;
    companyId: number | string;
    externalAppId: number | string;
    viewType: string;
    isReadOnly: boolean;
    isPopup: boolean;
    translate: (key: string) => string;
    /** the task never opened, so the caller should fall back to the url-based editor */
    onHandshakeFailed?: () => void;
}

// budget for each leg of the handshake: the editor announcing itself, and then opening the task
export const HANDSHAKE_TIMEOUT_MS = 15000;

// diagnostics only, so never the values - a message on this origin can carry the session
const messageShape = (data: any) => data !== null && typeof data === "object" ? Object.keys(data) : typeof data;

interface BnxtSession {
    target: BnxtEditorTarget;
    editRequest: BnxtEditRequest;
}

export function BnxtDocumentEditor(props: Readonly<BnxtDocumentEditorProps>) {
    const userConfiguration: any = useSelector((store) => reduxSelectors.getUsersData(store));
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const sentSessionRef = useRef<BnxtSession | null>(null);
    const [target, setTarget] = useState<BnxtEditorTarget | null>(null);
    const sentRequestIdRef = useRef<string | null>(null);
    const [editorReady, setEditorReady] = useState(false);
    const [taskOpen, setTaskOpen] = useState(false);
    const [sessionError, setSessionError] = useState(false);

    const {taskId, id, companyId, externalAppId, viewType, isReadOnly} = props;
    const {currentContext, currentUserId} = userConfiguration;
    const isAdministrator = userConfiguration.userRoles.systemAdministrator;

    useEffect(() => {
        let cancelled = false;
        const isDocumentView = viewType === DETAILS_VIEW_TYPE.DOCUMENT;
        const getEditor = isDocumentView ? Api.getDocumentOverviewEditor : Api.getDocumentEditor;

        setTarget(null);
        setTaskOpen(false);
        setSessionError(false);

        getEditor(taskId, companyId).then((response) => {
            if (cancelled) {
                return;
            }

            const editorTarget = parseEditorUrl(response?.url);

            if (!editorTarget) {
                setSessionError(true);
                return;
            }

            setTarget(editorTarget);
        }).catch(() => {
            if (!cancelled) {
                setSessionError(true);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [taskId, companyId, viewType]);

    // a new object here is what makes the openTask effect re-post, so everything the request reads has to be a dependency
    const session: BnxtSession | null = useMemo(() => target === null ? null : {
        target,
        editRequest: {
            companyId: (viewType === DETAILS_VIEW_TYPE.TASK ? companyId : currentContext) + "",
            systemId: externalAppId + "",
            userId: currentUserId + "",
            readonly: isReadOnly,
            administrator: isAdministrator,
            taskId: id
        }
    }, [target, id, companyId, externalAppId, viewType, isReadOnly, currentContext, currentUserId, isAdministrator]);

    // read through a ref so an inline callback from the parent cannot restart the timer on every render
    const onHandshakeFailedRef = useRef(props.onHandshakeFailed);
    onHandshakeFailedRef.current = props.onHandshakeFailed;

    // editorReady is a dependency so that opening the task gets a budget of its own once the editor has answered
    useEffect(() => {
        if (!target || taskOpen) {
            return;
        }

        const timeout = window.setTimeout(() => {
            console.warn(`${BNXT_LOG_PREFIX} the editor did not ${editorReady ? "open the task" : "report ready"} `
                + `within ${HANDSHAKE_TIMEOUT_MS}ms`, target.url);
            onHandshakeFailedRef.current?.();
        }, HANDSHAKE_TIMEOUT_MS);

        return () => window.clearTimeout(timeout);
    }, [target, editorReady, taskOpen]);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (!target || event.origin !== target.origin) {
                return;
            }

            // only the editor iframe itself may drive the handshake; any other window on the origin could spoof a ready
            if (event.source !== iframeRef.current?.contentWindow) {
                console.warn(`${BNXT_LOG_PREFIX} ignored a message from another window on the editor origin`,
                    messageShape(event.data));
                return;
            }

            if (isEditorReadyMessage(event.data)) {
                setEditorReady(true);
                return;
            }

            const result = readOpenTaskResult(event.data);

            if (!result) {
                console.warn(`${BNXT_LOG_PREFIX} unrecognised message from the editor`, messageShape(event.data));
                return;
            }

            // one answer per request, so a late one must not tear down an editor that is already open
            if (taskOpen) {
                console.warn(`${BNXT_LOG_PREFIX} ${result.command} after the task was already open`, result.requestId);
                return;
            }

            // a result for an openTask we no longer care about, e.g. a late answer for the previous task
            if (result.requestId !== sentRequestIdRef.current) {
                console.warn(`${BNXT_LOG_PREFIX} ${result.command} for a stale request`, result.requestId);
                return;
            }

            if (result.command === OPEN_TASK_SUCCESS) {
                setTaskOpen(true);
                return;
            }

            console.warn(`${BNXT_LOG_PREFIX} the editor could not open the task`, result.requestId);
            onHandshakeFailedRef.current?.();
        };

        window.addEventListener("message", onMessage, false);

        return () => window.removeEventListener("message", onMessage);
    }, [target, taskOpen]);

    useEffect(() => {
        if (!editorReady || !session || sentSessionRef.current === session) {
            return;
        }

        const message = buildOpenTaskMessage(session.target.approvalToken, session.editRequest);

        sentRequestIdRef.current = message.requestId;
        setTaskOpen(false);
        iframeRef.current?.contentWindow?.postMessage(message, session.target.origin);
        sentSessionRef.current = session;
    }, [editorReady, session]);

    const onIframeLoad = () => {
        console.log(`${BNXT_LOG_PREFIX} iframe loaded`, target?.url);

        // a load means a fresh document that has to announce itself again before we may post the session to it
        setEditorReady(false);
        setTaskOpen(false);
        sentSessionRef.current = null;
    };

    const frameClasses = classNames("document-editor-frame height-100", {"document-editor-popup": props.isPopup});

    if (sessionError) {
        return (
            <div className="height-100">
                <div className={frameClasses}>
                    <div className="d-flex justify-content-center mt-32">
                        <Notification
                            type={NotificationType.ERROR}
                            title={props.translate("popUp.documentEditor.errorTitle")}
                            description={props.translate("popUp.documentEditor.errorSuggestion")}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="height-100 position-relative">
            {!taskOpen &&
                <div className="position-absolute top-50 w-100">
                    <Loader size={LoaderSize.Medium}/>
                </div>
            }
            {target &&
                <iframe
                    ref={iframeRef}
                    onLoad={onIframeLoad}
                    id={"Bnxt-Document-Editor-Iframe"}
                    src={target.url}
                    title={"Document Editor"}
                    allow={"clipboard-read; clipboard-write"}
                    className={classNames("w-100", frameClasses)}
                />
            }
        </div>
    );
}

export default BnxtDocumentEditor;
